/**
 * Account switcher (header) — "Comptes gérés" dropdown. Lists the user's OWN
 * account plus every account they may act in via an accepted delegation
 * (GET /api/v1/delegations/accounts). Selecting a managed account sets the
 * acting-tenant scope (X-Acting-Tenant on every request, via
 * ActingTenantProvider) and reloads so ALL data re-fetches under the delegated
 * scope — mirroring the WorkspaceSwitcher brand-switch pattern.
 *
 * HIDDEN when the user has no delegated accounts (costs one cheap GET on
 * mount), so the header stays clean for the common single-account case. The
 * persistent "vous agissez dans…" banner (ActingTenantBanner) is separate and
 * always shows while a managed account is active, even if this control is
 * hidden after a reload.
 */

import { useEffect, useId, useRef, useState } from 'react';

import { delegatedAccountName, listDelegatedAccounts, type DelegatedAccount } from '@/lib/api/delegations';
import { onAbsent } from '@/lib/api/degrade';
import { useActingTenant } from '@/app/providers/ActingTenantProvider';
import { useT } from '@/i18n';

export function AccountSwitcher() {
  const { t } = useT();
  const { account: activeAccount, setAccount, clear } = useActingTenant();
  const activeOwnerId = activeAccount?.owner_tenant_id ?? null;
  const [accounts, setAccounts] = useState<DelegatedAccount[]>([]);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const panelId = useId();

  useEffect(() => {
    let alive = true;
    // A delegation backend that is not rolled out (404/501/503) degrades to
    // "no managed accounts" (control stays hidden); any real error is swallowed
    // to a hidden control too — this is a progressive-enhancement affordance,
    // never a blocking surface.
    void listDelegatedAccounts()
      .catch(onAbsent([] as DelegatedAccount[]))
      .then((list) => {
        if (!alive) return;
        setAccounts(list);
        // Reconcile the persisted acting scope with server truth: a revoked /
        // expired grant must not keep sending a dead X-Acting-Tenant.
        if (activeOwnerId && !list.some((a) => a.owner_tenant_id === activeOwnerId)) {
          clear();
          window.location.reload();
        }
      })
      .catch(() => {
        if (alive) setAccounts([]);
      });
    return () => {
      alive = false;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Nothing to switch to unless at least one delegation exists.
  if (accounts.length === 0) return null;

  const activeId = activeAccount?.owner_tenant_id ?? null;
  const activeLabel = activeAccount ? delegatedAccountName(activeAccount) : t('shell.app.layoutsAccountSwitcher.myAccount');

  const choose = (next: DelegatedAccount | null) => {
    const nextId = next?.owner_tenant_id ?? null;
    if (nextId === activeId) {
      setOpen(false);
      return;
    }
    setAccount(next);
    // Re-fetch everything in the newly-scoped account (own or delegated).
    window.location.reload();
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={t('shell.app.layoutsAccountSwitcher.activeAccountActivelabel', { activeLabel })}
        data-testid="account-switcher"
        className={`flex min-h-[44px] max-w-[12rem] items-center gap-1.5 rounded-token border px-2 py-1.5 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-ring ${
          activeAccount
            ? 'border-warning/60 bg-warning/10 text-foreground'
            : 'border-border bg-surface-2/40 text-foreground hover:bg-surface-2'
        }`}
      >
        <svg
          aria-hidden="true"
          viewBox="0 0 24 24"
          className="h-4 w-4 shrink-0 text-muted"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13A4 4 0 0 1 16 11" />
        </svg>
        <span className="hidden truncate sm:inline">{activeLabel}</span>
        <svg
          aria-hidden="true"
          viewBox="0 0 24 24"
          className="h-3.5 w-3.5 shrink-0 text-muted"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>
      {open && (
        <div
          id={panelId}
          className="absolute left-0 top-full z-50 mt-1 w-72 overflow-hidden rounded-token-lg border border-border bg-surface py-1 shadow-elevation-3"
        >
          <div className="px-3 py-2 text-[10px] font-semibold uppercase tracking-wide text-muted">{t('shell.app.layoutsAccountSwitcher.managedAccounts')}</div>
          <ul>
            <li>
              <button
                type="button"
                onClick={() => choose(null)}
                aria-current={activeId === null ? 'true' : undefined}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-foreground hover:bg-surface-2 focus:outline-none focus-visible:bg-surface-2 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
              >
                <span className="min-w-0 flex-1 truncate font-medium">{t('shell.app.layoutsAccountSwitcher.myAccount')}</span>
                {activeId === null && (
                  <span aria-hidden="true" className="text-primary">
                    ✓
                  </span>
                )}
              </button>
            </li>
            {accounts.map((acc) => (
              <li key={acc.owner_tenant_id}>
                <button
                  type="button"
                  onClick={() => choose(acc)}
                  aria-current={acc.owner_tenant_id === activeId ? 'true' : undefined}
                  className="flex w-full items-start gap-2 px-3 py-2 text-left text-sm text-foreground hover:bg-surface-2 focus:outline-none focus-visible:bg-surface-2 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
                >
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium">{delegatedAccountName(acc)}</span>
                    <span className="block truncate text-xs text-muted">
                      {acc.permissions.length}{' '}{t('shell.app.layoutsAccountSwitcher.permission')}{acc.permissions.length === 1 ? '' : 's'}{' '}{t('shell.app.layoutsAccountSwitcher.granted')}{acc.permissions.length === 1 ? '' : 's'}
                    </span>
                  </span>
                  {acc.owner_tenant_id === activeId && (
                    <span aria-hidden="true" className="mt-0.5 text-primary">
                      ✓
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
